import {
    MutableRefObject,
    useCallback,
    useState,
    useRef,
    useEffect,
    PropsWithChildren
} from "react";

import { css } from "@compiled/react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { FeatureExposed } from "@/utils/feature-exposed";
import { OptionalExceptFor } from "@/utils/typescript-utils.ts";

import { Tabs } from "./tab-contents/tabs.tsx";
import { TEAM_DATA, Team } from "./tab-contents/team-data.tsx";

export const DEFAULT_TEAM_DATA: ActiveTeamDataProps = {
    id: "default",
    videoLink:
        // eslint-disable-next-line local/no-non-orangelogic-images-eslint
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_FULL_LowBR.mp4",
    heroImage:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/32mac5076120jymg5y7rtwu4ru5g3s6l.webp",
    heroImageDimensions: {
        imageWidth: 1920,
        imageHeight: 1080
    }
};

export type ActiveTeamDataProps = OptionalExceptFor<
    Team,
    "heroImage" | "heroImageDimensions"
>;

export const HeroJiraCtaTemplate = ({ children }: PropsWithChildren) => {
    const tabsWrapperRef: MutableRefObject<HTMLDivElement | null> =
        useRef(null);

    const [selectedTeamIndex, setSelectedTeamIndex] = useState<
        number | undefined
    >(undefined);
    const [activeTeamData, setActiveTeamData] =
        useState<ActiveTeamDataProps>(DEFAULT_TEAM_DATA);

    // if a tab is currently selected and the user clicks outside of the
    // tabs container, display the default video from its beginning
    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                typeof selectedTeamIndex === "number" &&
                tabsWrapperRef.current &&
                !tabsWrapperRef.current.contains(event.target as Node)
            ) {
                setSelectedTeamIndex(undefined);
            }
        },
        [setSelectedTeamIndex]
    );

    useEffect(() => {
        // Attach the listener on component mount
        document.addEventListener("mousedown", handleClickOutside);

        // Detach the listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        if (typeof selectedTeamIndex !== "number") {
            setActiveTeamData(DEFAULT_TEAM_DATA);
            return;
        }
        setActiveTeamData(TEAM_DATA[selectedTeamIndex] || DEFAULT_TEAM_DATA);
        return;
    }, [selectedTeamIndex]);

    return (
        <UFOSegment name="homepage-v2-hero">
            <section id="hero-jira-cta" css={jiraHeroStyles}>
                <>
                    {children}
                    <FeatureExposed
                        experimentKey="gwp-1705-homepage-hero"
                        group="Test"
                    >
                        <Tabs
                            heroSelectedTeamIndex={selectedTeamIndex}
                            onSelectHeroTab={setSelectedTeamIndex}
                            tabsWrapperRef={tabsWrapperRef}
                            {...activeTeamData}
                        />
                    </FeatureExposed>
                </>
            </section>
        </UFOSegment>
    );
};

const jiraHeroStyles = css({
    overflow: "hidden"
});

HeroJiraCtaTemplate.displayName = "HeroJiraCtaTemplate";
